import React from 'react';
import { motion } from 'framer-motion';
import './Hero.css';
import { heroData } from '../../data/heroData';
import { FaLinkedin, FaGithub, FaInstagram, FaFacebook, FaTwitter } from 'react-icons/fa';
import { HiOutlineChevronDown } from 'react-icons/hi';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.35 }
    }
  };

  const itemLeft = {
    hidden: { opacity: 0, x: -48 },
    show: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', damping: 22, stiffness: 180 }
    }
  };

  const itemUp = {
    hidden: { opacity: 0, y: 32 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', damping: 24, stiffness: 160 }
    }
  };

  const imageAnim = {
    hidden: { opacity: 0, x: 60, scale: 0.9 },
    show: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { type: 'spring', damping: 20, stiffness: 120, delay: 0.4 }
    }
  };

  const socialItem = {
    hidden: { opacity: 0, scale: 0 },
    show: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', damping: 18, stiffness: 200, delay: 0.6 + i * 0.06 }
    })
  };

  return (
    <section id={heroData.header.id} className="hero">
      <div className="container heroContainer">
        <motion.div
          className="heroContent"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <div className="heroText">
            <motion.h5 variants={itemLeft}>{heroData.content.greeting}</motion.h5>
            <motion.h1 variants={itemLeft}>{heroData.content.name}</motion.h1>
            <motion.h2 variants={itemUp}>{heroData.content.title}</motion.h2>
            <motion.p variants={itemUp}>{heroData.content.subtitle}</motion.p>
            <motion.p className="heroDescription" variants={itemUp}>
              {heroData.content.description}
            </motion.p>
            <motion.p className="heroPhilosophy" variants={itemUp}>
              {heroData.content.philosophy}
            </motion.p>

            <motion.div className="heroButtons" variants={itemUp}>
              <motion.a
                href={heroData.content.cta.primary.href}
                className={heroData.content.cta.primary.className}
                whileHover={{ scale: 1.06, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {heroData.content.cta.primary.text}
              </motion.a>
              <motion.a
                href={heroData.content.cta.secondary.href}
                className={heroData.content.cta.secondary.className}
                whileHover={{ scale: 1.06, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {heroData.content.cta.secondary.text}
              </motion.a>
            </motion.div>

            <motion.div className="heroSocials" variants={container}>
              {[
                { Icon: FaLinkedin, href: heroData.socialLinks.linkedin },
                { Icon: FaGithub, href: heroData.socialLinks.github },
                { Icon: FaInstagram, href: heroData.socialLinks.instagram },
                { Icon: FaFacebook, href: heroData.socialLinks.facebook },
                { Icon: FaTwitter, href: heroData.socialLinks.twitter }
              ].map(({ Icon, href }, i) => (
                <motion.a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  custom={i}
                  variants={socialItem}
                  whileHover={{ scale: 1.2, y: -5, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <Icon />
                </motion.a>
              ))}
            </motion.div>
          </div>

          <motion.div className="heroImage" variants={imageAnim}>
            <div className="heroImageContainer">
              <motion.div
                className="heroImageGlow"
                animate={{
                  boxShadow: [
                    '0 0 40px rgba(20, 184, 166, 0.25)',
                    '0 0 70px rgba(45, 212, 191, 0.35)',
                    '0 0 40px rgba(20, 184, 166, 0.25)'
                  ]
                }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.img
                src={heroData.image.src}
                alt={heroData.image.alt}
                whileHover={{ scale: 1.03 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              />
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="scrollIndicator"
          onClick={() => scrollToSection('about')}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{
            opacity: { delay: 1.2 },
            y: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
          }}
        >
          <HiOutlineChevronDown />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
